import React, { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { CheckCircle2, XCircle, AlertTriangle, MinusCircle, ShieldCheck, Loader2 } from "lucide-react";

const ICONS = {
  pass: { Icon: CheckCircle2, cls: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
  fail_error: { Icon: XCircle, cls: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/30" },
  fail_warning: { Icon: AlertTriangle, cls: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30" },
  skip: { Icon: MinusCircle, cls: "text-slate-500", bg: "bg-slate-700/30 border-slate-700" },
};

export default function ValidatorPanel({ data, format }) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true); setErr("");
      try {
        const { data: r } = await api.post("/invoices/validate", { data, format });
        if (!cancelled) setRes(r);
      } catch (e) {
        if (!cancelled) setErr(formatErr(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const t = setTimeout(run, 300); // debounce
    return () => { cancelled = true; clearTimeout(t); };
  }, [JSON.stringify(data), format]);

  if (!res && loading) {
    return (
      <div className="card p-4 flex items-center gap-3" data-testid="validator-panel-loading">
        <Loader2 size={18} className="animate-spin text-blue-400"/>
        <span className="text-sm text-slate-300">EN-16931-Validierung läuft...</span>
      </div>
    );
  }

  if (err) {
    return <div className="card p-4 text-rose-200 text-sm" data-testid="validator-error">Validierung: {err}</div>;
  }

  if (!res) return null;

  const { summary, checks, ready } = res;
  const headerBg = ready ? "bg-emerald-500/10 border-emerald-500/30" : "bg-amber-500/10 border-amber-500/30";
  const headerIcon = ready ? CheckCircle2 : AlertTriangle;
  const HIcon = headerIcon;

  return (
    <div className="card" data-testid="validator-panel">
      <div className={`flex items-center justify-between gap-3 px-5 py-4 border-b ${headerBg}`}>
        <div className="flex items-center gap-3">
          <HIcon size={22} className={ready ? "text-emerald-400" : "text-amber-300"}/>
          <div>
            <div className="font-display font-semibold" data-testid="validator-status-label">
              {ready ? "EN 16931 konform – bereit zum Export" : "Prüfen Sie die rot markierten Felder"}
            </div>
            <div className="text-xs font-mono tracking-wider text-slate-400 mt-0.5">
              <span data-testid="validator-pass-count">{summary.passed}</span> / {summary.total} Checks bestanden
              {summary.errors > 0 && <> · <span className="text-rose-300" data-testid="validator-error-count">{summary.errors} Fehler</span></>}
              {summary.warnings > 0 && <> · <span className="text-amber-300" data-testid="validator-warn-count">{summary.warnings} Warnungen</span></>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`chip ${ready ? "chip-valid" : "chip-error"}`} data-testid="validator-overall-chip">
            <ShieldCheck size={12}/>{ready ? "GÜLTIG" : "ENTWURF"}
          </span>
          <button className="btn-ghost text-xs" onClick={()=>setExpanded(e=>!e)} data-testid="validator-expand-button">
            {expanded ? "Weniger" : `Alle ${summary.total} Checks`}
          </button>
        </div>
      </div>

      <div className="p-4 grid sm:grid-cols-2 gap-2">
        {(expanded ? checks : checks.filter(c => c.status !== "pass")).slice(0, expanded ? 100 : 6).map((c, i) => {
          const key = c.status === "fail" ? `fail_${c.level}` : c.status;
          const style = ICONS[key] || ICONS.skip;
          const Ic = style.Icon;
          return (
            <div key={c.code + i} className={`flex items-start gap-2 p-2.5 rounded-lg border ${style.bg}`} data-testid={`validator-check-${c.code}`}>
              <Ic size={16} className={`${style.cls} shrink-0 mt-0.5`}/>
              <div className="min-w-0">
                <div className="text-sm text-slate-100 font-medium truncate" title={c.label}>{c.label}</div>
                {c.hint && c.status !== "pass" && <div className="text-xs text-slate-400 mt-0.5">{c.hint}</div>}
              </div>
            </div>
          );
        })}
        {!expanded && checks.every(c => c.status === "pass") && (
          <div className="col-span-full text-sm text-emerald-300/90 py-2 text-center">
            Alle Pflichtfelder erfüllt – Sie können die E-Rechnung exportieren.
          </div>
        )}
      </div>
    </div>
  );
}
